import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetServicesQuery,
  useGetSpecialOffersQuery,
} from "../redux/services/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingButtons from "../components/FloatingButtons";
import { createExcerpt } from "../utils/excerpt";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../contexts/LanguageContext";
import AOS from "aos";
import "aos/dist/aos.css";

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const searchTimeoutRef = useRef(null);
  const { t, isArabic } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  // Use RTK Query to get all services and offers (fetch once, filter in frontend)
  const { data: allServicesData, isLoading: isLoadingAllServices } =
    useGetServicesQuery();
  const { data: allOffersData, isLoading: isLoadingAllOffers } =
    useGetSpecialOffersQuery();

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });
  }, []);

  // Update search query from URL params
  useEffect(() => {
    const query = searchParams.get("q");
    if (query) {
      setSearchQuery(query);
    } else {
      setSearchQuery("");
    }
  }, [searchParams]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Auto-update URL params as user types (debounced) - filtering happens in frontend
    const trimmedQuery = query.trim();
    if (trimmedQuery.length > 2) {
      // Debounce URL update - wait 500ms after user stops typing
      searchTimeoutRef.current = setTimeout(() => {
        setSearchParams({ q: trimmedQuery });
      }, 500);
    } else if (trimmedQuery.length === 0) {
      // Clear search params when input is cleared - show all items
      setSearchParams({});
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery.length > 2) {
      setSearchParams({ q: trimmedQuery });
    } else {
      // Clear search params if query is too short - this will show all items
      setSearchParams({});
      setSearchQuery("");
    }
  };

  const handleServiceClick = (serviceId) => {
    navigate(getLocalizedPath(`/service/${serviceId}`));
  };

  const handleOfferClick = (offerId) => {
    navigate(getLocalizedPath(`/offer/${offerId}`));
  };

  // Determine if we have a valid search query
  const hasValidQuery = searchQuery.trim().length > 2;
  const searchTerm = hasValidQuery ? searchQuery.trim().toLowerCase() : "";

  // Filter services and offers in frontend based on search query
  const allServices =
    allServicesData?.services?.filter((service) => service.is_active) || [];
  const allOffers =
    allOffersData?.offers?.filter((offer) => offer.is_active) || [];

  // Filter services based on search query
  const services = hasValidQuery
    ? allServices.filter((service) => {
        const title = (service.title || "").toLowerCase();
        const content = (service.content || "").toLowerCase();
        const titleAr = (service.title_ar || "").toLowerCase();
        const contentAr = (service.content_ar || "").toLowerCase();
        return (
          title.includes(searchTerm) ||
          content.includes(searchTerm) ||
          titleAr.includes(searchTerm) ||
          contentAr.includes(searchTerm)
        );
      })
    : allServices;

  // Filter offers based on search query
  const offers = hasValidQuery
    ? allOffers.filter((offer) => {
        const title = (offer.title || "").toLowerCase();
        const content = (offer.content || "").toLowerCase();
        const titleAr = (offer.title_ar || "").toLowerCase();
        const contentAr = (offer.content_ar || "").toLowerCase();
        return (
          title.includes(searchTerm) ||
          content.includes(searchTerm) ||
          titleAr.includes(searchTerm) ||
          contentAr.includes(searchTerm)
        );
      })
    : allOffers;

  const isLoading = isLoadingAllServices || isLoadingAllOffers;
  const hasResults = services.length > 0 || offers.length > 0;
  const totalResults = services.length + offers.length;

  return (
    <div className="search-page service-detail-page">
      <Header />
      <div className="container">
        {/* Page Title */}
        <div
          className="page-title-section"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          <h1>{t("nav.search")}</h1>
          <p>{t("nav.searchPlaceholder")}</p>
        </div>

        {/* Search Form */}
        <div
          className="search-page-form"
          data-aos="fade-up"
          data-aos-delay="200"
        >
          <form onSubmit={handleSearchSubmit} className="search-form">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder={t("nav.searchPlaceholder")}
                value={searchQuery}
                onChange={handleSearchChange}
                className="search-input"
                autoComplete="off"
                autoFocus
                dir={isArabic ? "rtl" : "ltr"}
              />
              <button
                type="submit"
                className="search-submit"
                aria-label="Search"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  width="22"
                  height="22"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </button>
            </div>
          </form>
        </div>

        {/* Results - Show all when no search, filtered when searching */}
        <div
          className="search-results-container"
          data-aos="fade-up"
          data-aos-delay="300"
        >
          {isLoading ? (
            <div className="search-loading">
              <div className="loading-spinner"></div>
              <p>{t("services.loadingServices")}</p>
            </div>
          ) : hasResults ? (
            <>
              <div className="search-results-header">
                <p>
                  {hasValidQuery
                    ? `${t("nav.searchResults")} (${totalResults})`
                    : `${t("nav.ourServices")} & ${t(
                        "nav.specialOffers"
                      )} (${totalResults})`}
                </p>
              </div>

              {/* Services Results */}
              {services.length > 0 && (
                <div className="search-results-section">
                  <h2 className="search-section-title">
                    {t("nav.ourServices")} ({services.length})
                  </h2>
                  <div className="services-grid-page">
                    {services.map((service, index) => (
                      <div
                        key={`service-${service.id}`}
                        className="service-item clickable-service-item"
                        data-aos="fade-up"
                        data-aos-delay={`${300 + index * 100}`}
                        onClick={() => handleServiceClick(service.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="service-content">
                          <div className="service-title-box">
                            <span>
                              {isArabic && service.title_ar
                                ? service.title_ar
                                : service.title}
                            </span>
                          </div>
                          <div className="service-description">
                            {createExcerpt(
                              isArabic && service.content_ar
                                ? service.content_ar
                                : service.content,
                              20
                            )}
                          </div>
                        </div>
                        <div className="service-image-container">
                          {service.image_url ? (
                            <img
                              src={service.image_url}
                              alt={
                                isArabic && service.title_ar
                                  ? service.title_ar
                                  : service.title
                              }
                              className="service-image"
                            />
                          ) : (
                            <div className="service-placeholder">
                              <span>{t("services.servicePic")}</span>
                            </div>
                          )}
                        </div>
                        {index < services.length - 1 && (
                          <div className="service-separator"></div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Offers Results */}
              {offers.length > 0 && (
                <div className="search-results-section">
                  <h2 className="search-section-title">
                    {t("nav.specialOffers")} ({offers.length})
                  </h2>
                  <div className="services-grid-page">
                    {offers.map((offer, index) => (
                      <div
                        key={`offer-${offer.id}`}
                        className="service-item clickable-service-item"
                        data-aos="fade-up"
                        data-aos-delay={`${
                          300 + (services.length + index) * 100
                        }`}
                        onClick={() => handleOfferClick(offer.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="service-content">
                          <div className="service-title-box">
                            <span>
                              {isArabic && offer.title_ar
                                ? offer.title_ar
                                : offer.title}
                            </span>
                          </div>
                          <div className="service-description">
                            {createExcerpt(
                              isArabic && offer.content_ar
                                ? offer.content_ar
                                : offer.content,
                              20
                            )}
                          </div>
                        </div>
                        <div className="service-image-container">
                          {offer.image_url ? (
                            <img
                              src={offer.image_url}
                              alt={
                                isArabic && offer.title_ar
                                  ? offer.title_ar
                                  : offer.title
                              }
                              className="service-image"
                            />
                          ) : (
                            <div className="service-placeholder">
                              <span>{t("offers.offerPic")}</span>
                            </div>
                          )}
                        </div>
                        {index < offers.length - 1 && (
                          <div className="service-separator"></div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="search-no-results">
              <p>
                {hasValidQuery ? t("nav.noResults") : t("services.noServices")}
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default SearchPage;
